/**
 * Asks permission to keep the log in protected storage, in our own words
 * first.
 *
 * The browser's own dialog cannot be restyled or reworded, so the job here
 * is to make sure it never arrives cold: by the time it appears the family
 * has read why it matters, been told the browser is about to ask, and
 * tapped a button requesting it. That turns a confusing interruption into
 * the expected next step.
 *
 * It waits until there is a log worth protecting, and stands down while
 * the install card is showing so Home never stacks two asks.
 */
import { useEffect, useState } from 'react';
import { useT } from '../i18n/i18n';
import { useApp } from '../lib/store';
import {
  persistPermissionState,
  requestPersistence,
  storageStatus,
  useInstall,
} from '../lib/install';
import { Icons } from './ui';

/** Entries logged before we bring it up: enough that losing them would sting. */
const MIN_EVENTS = 5;
const DISMISS_KEY = 'persistPromptDismissedAt';
const DISMISS_DAYS = 14;

function dismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  const at = Number(raw);
  return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_DAYS * 86_400_000;
}

export default function PersistCard() {
  const t = useT();
  const { events } = useApp();
  const install = useInstall();
  const [eligible, setEligible] = useState(false);
  const [dismissed, setDismissed] = useState(dismissedRecently);
  const [asking, setAsking] = useState(false);
  const [outcome, setOutcome] = useState<'granted' | 'declined' | null>(null);

  useEffect(() => {
    void (async () => {
      const [status, permission] = await Promise.all([storageStatus(), persistPermissionState()]);
      // Nothing to ask for if it is unsupported, already on, or already
      // refused: re-asking a "denied" just nags at someone who said no.
      setEligible(status.supported && !status.persisted && permission !== 'denied');
    })();
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const onAsk = async () => {
    setAsking(true);
    try {
      const status = await requestPersistence();
      setOutcome(status.persisted ? 'granted' : 'declined');
      if (status.persisted) setEligible(false);
    } finally {
      setAsking(false);
    }
  };

  if (outcome === 'granted') {
    return (
      <section className="card animate-float-up flex items-center gap-3 p-4">
        <span className="shrink-0 rounded-xl bg-good-soft p-2 text-good">
          {Icons.heart('h-5 w-5')}
        </span>
        <p className="text-sm font-bold text-good">{t.persist.granted}</p>
      </section>
    );
  }

  // One ask at a time: the install card matters more, especially on iOS.
  if (!eligible || dismissed || install.shouldOffer) return null;
  if (events.length < MIN_EVENTS) return null;

  return (
    <section className="card animate-float-up relative overflow-hidden p-4">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t.common.actions.close}
        className="absolute right-2 top-2 rounded-full p-2 text-ink-faint hover:bg-surface-2"
      >
        {Icons.x('h-4 w-4')}
      </button>

      <div className="flex items-start gap-3 pr-8">
        <span className="mt-0.5 shrink-0 rounded-xl bg-primary-soft p-2 text-primary">
          {Icons.heart('h-5 w-5')}
        </span>
        <div className="min-w-0">
          <h2 className="text-base">{t.persist.title}</h2>
          <p className="mt-1 text-sm text-ink-soft">{t.persist.body(events.length)}</p>
        </div>
      </div>

      {/* Say plainly what is about to happen, so the browser dialog reads as
          the expected next step rather than an interruption. */}
      <p className="mt-3 rounded-xl bg-accent-soft p-3 text-sm text-accent">
        {t.persist.whatHappensNext}
      </p>

      {outcome === 'declined' ? (
        <p role="status" className="mt-2 text-sm text-ink-soft">
          {t.persist.declined}
        </p>
      ) : null}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => void onAsk()}
          disabled={asking}
          className="btn-primary min-h-[44px] flex-1 px-4 disabled:opacity-60"
        >
          {asking ? t.persist.asking : t.persist.confirm}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="btn-secondary min-h-[44px] px-4"
        >
          {t.persist.notNow}
        </button>
      </div>
    </section>
  );
}
